/* ==========================================================================
   Brazos Site Supply - freight zone lookup, PDP calculator, freight page
   --------------------------------------------------------------------------
   ONE rate table, read from a shop metafield and inlined by the section as
   <script type="application/json" data-bss-rates>. There must never be two.
   The theme previously carried a second, uncalibrated table in
   assets/freight-zones.json that read back $185 for a ZIP the checkout charges
   $325 for. If you see that file still in the theme, delete it.

   PRIVACY
     The ZIP is used for one calculation in page memory. It is never appended
     to the URL, written to the cart, stored, or published to analytics. The
     existing analytics deny-list already names `zip`; this module never calls
     the tracker at all.

   HONESTY
     Destinations outside the lower 48 are deliberately unmapped and route to a
     written quote rather than an extrapolated number. A ZIP with no calibrated
     rate does the same. Guessing here is how a customer gets re-billed after
     delivery, which is the failure this whole page exists to prevent.
   ========================================================================== */

window.BSSFreight = (function () {
  'use strict';

  /* zip3 ranges, from freight/zone-bands.csv. Kept here rather than in the
     rate JSON because the geography changes far less often than the money. */
  var BANDS = [
    [1, [[700, 729], [730, 749], [750, 799], [885, 885]]],
    [2, [[270, 299], [300, 349], [350, 369], [370, 385], [386, 397],
         [398, 399], [400, 427], [630, 658], [660, 679]]],
    [3, [[430, 459], [460, 479], [480, 499], [500, 528], [530, 549],
         [550, 567], [570, 577], [580, 588], [590, 599], [600, 629],
         [680, 693], [800, 816], [820, 831], [870, 884]]],
    [4, [[10, 69], [70, 89], [100, 149], [150, 196], [197, 199],
         [200, 219], [220, 246], [247, 268]]],
    [5, [[833, 838], [840, 847], [850, 865], [889, 898], [900, 961],
         [970, 979], [980, 994]]]
  ];

  /* Alaska, Hawaii, Puerto Rico, the Virgin Islands and APO/FPO. Unmapped on
     purpose - see the header. */
  var OFF_MAP = [[995, 999], [967, 968], [6, 9], [962, 966]];

  var QUOTE_EMAIL = 'hello@brazossitesupply.com';

  function money(n) {
    return '$' + Math.round(n).toLocaleString('en-US') + '.00';
  }

  function zoneFor(zip) {
    zip = String(zip || '').trim();
    if (!/^\d{5}$/.test(zip)) {
      return { error: 'Enter a five-digit ZIP code.' };
    }
    var z3 = parseInt(zip.slice(0, 3), 10);
    var i, j;
    for (i = 0; i < OFF_MAP.length; i++) {
      if (z3 >= OFF_MAP[i][0] && z3 <= OFF_MAP[i][1]) {
        return { error: 'That destination is outside the lower 48. We quote those by hand '
                      + 'rather than guess - email ' + QUOTE_EMAIL + '.' };
      }
    }
    for (i = 0; i < BANDS.length; i++) {
      for (j = 0; j < BANDS[i][1].length; j++) {
        if (z3 >= BANDS[i][1][j][0] && z3 <= BANDS[i][1][j][1]) return { zone: BANDS[i][0] };
      }
    }
    return { error: 'We do not have a published rate for that ZIP yet, so it routes to a '
                  + 'written quote rather than a guess.' };
  }

  function bandFor(lb) {
    return lb <= 150 ? 0 : (lb <= 350 ? 1 : 2);
  }

  function ratesFrom(root) {
    var el = (root || document).querySelector('[data-bss-rates]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* ---------------------------------------------------------------- PDP */
  function initCalculator(section) {
    var zones = ratesFrom(section);
    if (!zones) return;

    var input   = section.querySelector('[data-bss-zip]');
    var go      = section.querySelector('[data-bss-go]');
    var help    = section.querySelector('[data-bss-help]');
    var out     = section.querySelector('[data-bss-out]');
    if (!input || !go || !out) return;

    var helpDefault = help ? help.textContent : '';
    var unitPrice = parseFloat(section.dataset.unitPrice || '0');
    var unitLb    = parseFloat(section.dataset.unitLb || '0');

    /* A product with no confirmed crate weight cannot be priced. Say so rather
       than pricing it from a guess. */
    if (!unitLb) {
      section.setAttribute('data-bss-unpriceable', '1');
      return;
    }

    function price() {
      var r = zoneFor(input.value);
      if (r.error) {
        out.hidden = true;
        if (help) { help.textContent = r.error; help.classList.add('is-error'); }
        return;
      }
      if (help) { help.textContent = helpDefault; help.classList.remove('is-error'); }

      var z = zones[r.zone - 1];
      var b = bandFor(unitLb);
      var freight = z.rates[b];

      section.querySelector('[data-bss-zone]').textContent =
        'ZONE ' + z.n + ' - ' + z.name.toUpperCase();
      section.querySelector('[data-bss-band]').textContent = 'Freight, ' + z.bands[b] + ' band';
      section.querySelector('[data-bss-freight]').textContent = money(freight);

      /* Accessorial lines are built as nodes, not as an HTML string, so a
         merchant-editable label can never inject markup into the quote. */
      var acc = 0;
      var accHost = section.querySelector('[data-bss-acc]');
      accHost.textContent = '';
      Array.prototype.forEach.call(section.querySelectorAll('[data-acc]'), function (cb) {
        if (!cb.checked) return;
        var cost = parseFloat(cb.dataset.cost || '0');
        acc += cost;
        var line = document.createElement('div');
        line.className = 'bss-qline';
        var s = document.createElement('span');
        s.textContent = cb.closest('.bss-opt').querySelector('.bss-opt__title').textContent.trim();
        var b2 = document.createElement('b');
        b2.textContent = cost ? money(cost) : '$0.00';
        line.appendChild(s);
        line.appendChild(b2);
        accHost.appendChild(line);
      });

      section.querySelector('[data-bss-total]').textContent = money(unitPrice + freight + acc);
      var transit = section.querySelector('[data-bss-transit]');
      if (transit) {
        transit.textContent = 'Transit ' + z.transit + ' business days after pickup. This is the '
          + 'charge, not an estimate: we do not raise it for ordinary carrier variation.';
      }
      out.hidden = false;
    }

    go.addEventListener('click', price);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); price(); } });
    Array.prototype.forEach.call(section.querySelectorAll('[data-acc]'), function (cb) {
      cb.addEventListener('change', function () { if (!out.hidden) price(); });
    });
  }

  /* ------------------------------------------------------- freight page */
  function initFinder(section) {
    var zones = ratesFrom(section);
    if (!zones) return;

    var input = section.querySelector('[data-bss-zip2]');
    var go    = section.querySelector('[data-bss-go2]');
    var out   = section.querySelector('[data-bss-out2]');
    if (!input || !go || !out) return;

    function find() {
      var r = zoneFor(input.value);
      Array.prototype.forEach.call(section.querySelectorAll('.bss-rates tbody tr'), function (t) {
        t.classList.remove('is-on');
      });
      Array.prototype.forEach.call(section.querySelectorAll('.bss-zband'), function (b) {
        b.classList.remove('is-on');
      });
      if (r.error) { out.textContent = r.error; return; }

      var z = zones[r.zone - 1];
      var row  = section.querySelector('.bss-rates tbody tr[data-z="' + z.n + '"]');
      var band = section.querySelector('#bss-zb-' + z.n);
      if (row) row.classList.add('is-on');
      if (band) band.classList.add('is-on');

      out.textContent = '';
      var b = document.createElement('b');
      b.textContent = 'Zone ' + z.n + ' - ' + z.name + '.';
      out.appendChild(b);
      out.appendChild(document.createTextNode(
        ' One 6 ft table, 218 lb, delivers for ' + money(z.rates[1]).replace('.00', '')
        + ' to a commercial dock. Transit ' + z.transit + ' business days.'));
    }

    go.addEventListener('click', find);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); find(); } });
  }

  function boot(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-bss-calc]'), initCalculator);
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-bss-finder]'), initFinder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  return { zoneFor: zoneFor, bandFor: bandFor, money: money };
})();
